import { useEffect, useState } from "react"
import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import Alert from "../../../alert";
import bottyHasIssues from '../../../../../../../../assets/images/ai-onboarding/botty-has-issues.svg';
import bottyCheckingIcon from '../../../../../../../../assets/images/ai-onboarding/botty-checking.svg';
import bottyHasNoIssues from '../../../../../../../../assets/images/ai-onboarding/botty-checking-no-issues.svg';
import {
    Button,
    FormGroup,
    Label,
    Input,
    Spinner,
    Alert as BootstrapAlert
} from "reactstrap";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import AIOnboardingService from "src/modules/3rd-party-management/apis/admin/AIOnboardingService";
import AICheckChartResponse from "./components/AICheckChartResponse";
import ConfirmImportLoading from "./components/ConfirmImportLoading";

const StatisticsComponent = ({
    t,
    prepareStatusData,
    onNextButtonClicked,
    onBackButtonClicked,
    onCancelButtonClicked
}) => {
    const [confirmed, setConfirmed] = useState(false);

    const startProcessMutation = useMutation({
        mutationFn: async () => {
            const service = AIOnboardingService.getInstance();
            return await service.startProcess();
        },
        onError: () => {
            toast(t("An error occurred while starting process."), {
                type: "error"
            });
        },
        onSuccess: () => {
            onNextButtonClicked()
        }
    });

    const result = prepareStatusData?.result;

    const hasIssue = result?.issues?.length > 0;

    return (
        <>
            {hasIssue ? (
                <Alert
                    icon={bottyHasIssues}
                    title='Confirm to start'
                    message={(
                        <>
                            <p>
                                {t("Thanks for uploading your data. As I've started processing it, I've noticed a few areas that could be improved. The completeness of your data directly affects the accuracy of our analysis, so let’s take a look")}
                            </p>
                        </>
                    )}
                />
            ) : (
                <Alert
                    icon={bottyHasNoIssues}
                    title='Great'
                    message={(
                        <>
                            <p>
                                {t(
                                    'The uploaded file has been successfully received and is ready for processing'
                                )}
                            </p>
                        </>
                    )}
                />
            )}

            <div className="ai-onboarding-content-body">
                <div>
                    {hasIssue ? (
                        <AICheckChartResponse
                            supplierMissing={result?.supplier_missing}
                            productMissing={result?.product_missing}
                            linking={result?.linking}
                        />
                    ) : (
                        <BootstrapAlert
                            color="info"
                        >
                            <strong>
                                {t('Please confirm to start the import.')}
                            </strong> <br />
                            {t('This action cannot be undone.')}
                        </BootstrapAlert>
                    )}

                    <FormGroup check>
                        <Label check>
                            <Input
                                type="checkbox"
                                checked={confirmed}
                                onChange={(e) => setConfirmed(e.target.checked)}
                            />
                            {t('I would still like to start the import process and clean up my data later.')}
                        </Label>
                    </FormGroup>
                </div>

                <div className="ai-onboarding-tab-actions">
                    <div
                        className="d-flex justify-content-center align-items-center gap-2"
                    >
                        <Button
                            outline
                            color="secondary"
                            onClick={onBackButtonClicked}>
                            {t('Back')}
                        </Button>

                        <Button
                            disabled={!confirmed || startProcessMutation.isLoading}
                            color="primary"
                            onClick={startProcessMutation.mutate}
                        >
                            {startProcessMutation.isLoading && (
                                <Spinner
                                    animation="border"
                                    variant="white"
                                    size="sm"
                                    className="me-1"
                                />
                            )}
                            {t('Next')}
                        </Button>
                    </div>

                    <Button
                        color="secondary"
                        outline
                        onClick={onCancelButtonClicked}
                    >
                        {t('Cancel')}
                    </Button>
                </div>
            </div>
        </>
    )
}

const Confirmation = ({
    t,
    onNextButtonClicked,
    onBackButtonClicked,
    onCancelButtonClicked
}) => {
    const [ fetchPrepareStatus, setFetchPrepareStatus ] = useState(true);
    const [ fetchProcessingStatus, setFetchProcessingStatus ] = useState(false);

    const {
        data: prepareStatusData,
        isError: fetchPrepareStatusFaild
    } = useQuery({
        queryKey: ['3rd-party-management-ai-onboarding-prepare-status'],
        queryFn: async () => {
            const service = AIOnboardingService.getInstance();

            return await service.getPrepareStatus();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        retry: 0,
        enabled: fetchPrepareStatus,
        refetchInterval: 7000,
        onError: () => {
            toast(t('An error occurred while fetching the current status of the operation.'), {
                type: 'error'
            });

            setFetchPrepareStatus(false);
        }
    });

    const {
        data: proccessingStatusData
    } = useQuery({
        queryKey: ['3rd-party-management-ai-onboarding-processing-current-state'],
        queryFn: async () => {
            const service = AIOnboardingService.getInstance();

            return await service.getCurrentStep();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        retry: 0,
        enabled: fetchProcessingStatus,
        refetchInterval: 5000,
        onError: () => {
            toast(t('An error occurred while fetching the current status of the operation.'), {
                type: 'error'
            });
        }
    })

    useEffect(() => {
        if (prepareStatusData?.status === 'SUCCESS' || prepareStatusData?.status === 'FAILURE') {
            setFetchPrepareStatus(false);
        }

        if (prepareStatusData?.status === 'SUCCESS') {
            setFetchProcessingStatus(true);
        }
    }, [
        prepareStatusData
    ]);

    useEffect(() => {
        if (proccessingStatusData?.prepareTaskStatus === 'completed') {
            setFetchProcessingStatus(false);
        }
    }, [
        proccessingStatusData
    ]);

    if (fetchPrepareStatus || fetchProcessingStatus) {
        return (
            <>
                <Alert
                    icon={bottyCheckingIcon}
                    title='I’m analyzing your suppliers and product files'
                    message={(
                        <>
                            <p>
                                {t('This may take a little while')} — {t('please be patient while I check your data for completeness and consistency.')}
                            </p>
                        </>
                    )}
                />

                <div className="ai-onboarding-content-body">
                    <ConfirmImportLoading />

                    <div className="ai-onboarding-tab-actions">
                        <div
                            className="d-flex justify-content-center align-items-center gap-2"
                        >
                            <Button
                                outline
                                color="secondary"
                                disabled
                                onClick={onBackButtonClicked}>
                                {t('Back')}
                            </Button>

                            <Button
                                disabled
                                color="primary"
                            >
                                {t('Next')}
                            </Button>
                        </div>

                        <Button
                            color="secondary"
                            outline
                            onClick={onCancelButtonClicked}
                        >
                            {t('Cancel')}
                        </Button>
                    </div>
                </div>
            </>
        )
    }

    if (fetchPrepareStatusFaild || prepareStatusData?.status === 'FAILURE') {
        return (
            <>
                <Alert
                    icon={bottyHasIssues}
                    title='Validation Failed'
                    message={(
                        <p>
                            {t('The uploaded files could not be processed due to missing data or formatting issues. Please ensure the supplier and product files contain valid data and follow the required template, then try again.')}
                        </p>
                    )}
                />

                <div className="ai-onboarding-content-body">
                    <div className="ai-onboarding-tab-actions">
                        <div
                            className="d-flex justify-content-center align-items-center gap-2"
                        >
                            <Button
                                outline
                                color="secondary"
                                onClick={onBackButtonClicked}>
                                {t('Back')}
                            </Button>

                            <Button
                                disabled={true}
                                color="primary"
                            >
                                {t('Next')}
                            </Button>
                        </div>

                        <Button
                            color="secondary"
                            outline
                            onClick={onCancelButtonClicked}
                        >
                            {t('Cancel')}
                        </Button>
                    </div>
                </div>
            </>
        )
    }

    return (
        <StatisticsComponent
            t={t}
            prepareStatusData={prepareStatusData}
            onNextButtonClicked={onNextButtonClicked}
            onBackButtonClicked={onBackButtonClicked}
            onCancelButtonClicked={onCancelButtonClicked}
        />
    )

}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return {
        user: state.Login.user,
        token: token
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(Confirmation))
)